import { useEffect, useState } from "react";
import {
  ChevronRight, CircleUserRound, HelpCircle, Info,
  LogOut, Pencil, X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { logoutUser } from "../auth";
import UserLayout from "../components/Userlayout";
import "./ProfileResponsive.css";

const fieldClass = "agp-profile-field";

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: "", email: "", workshopName: "", mobile: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (mounted) setProfile(data?.user || data || {});
      } catch {
        if (mounted) setMessage("Could not load your profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) { setEditing(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, saving]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const { data } = await api.patch("/users/profile", profile);
      const updated = data?.user || profile;
      setProfile(updated);
      const old = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...old, ...updated }));
      setMessage("Profile updated successfully.");
      setEditing(false);
    } catch (err) {
      setMessage(err?.response?.data?.message || "Could not update profile.");
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logoutUser();
      navigate("/", { replace: true });
    } catch {
      setMessage("Could not log out. Please try again.");
      setLoggingOut(false);
    }
  };

  const settings = [
    { icon: CircleUserRound, title: "My Account", sub: "Make changes to your account", action: () => setEditing(true) },
    { icon: HelpCircle, title: "Help & Support", sub: "Contact our support team", action: () => navigate("/support") },
  ];

  const displayName = loading ? "Loading…" : profile.name || "Your name";
  const initials = (profile.name || "U").trim().slice(0, 1).toUpperCase();

  return (
    <UserLayout>
      <main className="agp-profile-page">
        <div className="agp-profile-page__content">


          <section className="agp-profile-identity" aria-label="Account summary">
            <div className="agp-profile-identity__avatar" aria-hidden="true">{initials}</div>
            <div className="agp-profile-identity__details">
              <strong>{displayName}</strong>
              <span>{profile.mobile || profile.email || "No contact information added"}</span>
            </div>
            <button className="agp-profile-identity__edit" type="button" onClick={() => setEditing(true)} aria-label="Edit profile">
              <Pencil size={19} />
            </button>
          </section>

          <section className="agp-profile-settings" aria-label="Account settings">
            {settings.map(({ icon: Icon, title, sub, action, disabled, toggle }) => (
              <button
                key={title}
                type="button"
                onClick={action}
                disabled={disabled}
                aria-label={disabled ? `${title} — not available yet` : title}
                className={`agp-profile-settings__row ${disabled ? "agp-profile-settings__row--disabled" : ""}`}
              >
                <span className="agp-profile-settings__icon"><Icon size={19} strokeWidth={1.6}/></span>
                <span className="agp-profile-settings__label"><strong>{title}</strong><small>{sub}</small></span>
                {toggle ? <span className="agp-profile-settings__toggle" aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
              </button>
            ))}
          </section>

          <section className="agp-profile-bottom" aria-label="Other options">
            <button type="button" className="agp-profile-settings__row" onClick={() => navigate("/about")}>
              <span className="agp-profile-settings__icon"><Info size={19} strokeWidth={1.6}/></span>
              <span className="agp-profile-settings__label"><strong>About App</strong><small>About FindAutoParts</small></span>
              <ChevronRight size={18} />
            </button>
            <button type="button" disabled={loggingOut} className="agp-profile-settings__row" onClick={logout}>
              <span className="agp-profile-settings__icon"><LogOut size={19} strokeWidth={1.6}/></span>
              <span className="agp-profile-settings__label"><strong>{loggingOut ? "Logging out…" : "Log out"}</strong><small>Further secure your account for safety</small></span>
              <ChevronRight size={18} />
            </button>
          </section>
          {message && <p className="agp-profile-status" role="status">{message}</p>}
        </div>

        {editing && (
          <div className="agp-modal-backdrop agp-profile-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) setEditing(false); }}>
            <form onSubmit={save} className="agp-profile-edit-modal" aria-label="Edit account information">
              <div className="agp-profile-modal-title">
                <div><h2>My Account</h2><p>Update your personal information.</p></div>
                <button type="button" onClick={() => setEditing(false)} disabled={saving} aria-label="Close edit profile"><X size={21}/></button>
              </div>
              {loading ? <p>Loading profile…</p> : (
                <div className="agp-profile-form-grid">
                  {[["name", "Full name"], ["email", "Email address"], ["workshopName", "Workshop name"], ["mobile", "Mobile number"]].map(([key, label]) => (
                    <label key={key}>{label}<input className={fieldClass} value={profile[key] || ""} onChange={(e) => setProfile((prev) => ({ ...prev, [key]: e.target.value }))} /></label>
                  ))}
                </div>
              )}
              <button type="submit" disabled={saving || loading} className="agp-profile-save-btn">{saving ? "Saving…" : "Save Changes"}</button>
            </form>
          </div>
        )}

      </main>
    </UserLayout>
  );
}
